import React from 'react';

import { SORT_OPTIONS, SortKey } from './resultsView';
import styles from './styles.module.css';

export type ResultsView = 'list' | 'calendar';

interface DisplayControls {
  sort: SortKey;
  onSortChange: (next: SortKey) => void;
  view: ResultsView;
  onViewChange: (next: ResultsView) => void;
}

interface ResultsToolbarProps {
  /** The filter pills and chips, in row order. */
  children: React.ReactNode;
  /** "Sort by" and the list / calendar toggle, right-aligned; left out when there is nothing to sort. */
  display?: DisplayControls;
}

const ListIcon: React.FC = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' aria-hidden>
    <path
      d='M2 3.5h10M2 7h10M2 10.5h10'
      stroke='currentColor'
      strokeWidth='1.4'
    />
  </svg>
);

const CalendarIcon: React.FC = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' aria-hidden>
    <rect
      x='2'
      y='3'
      width='10'
      height='9'
      rx='1.5'
      stroke='currentColor'
      strokeWidth='1.3'
    />
    <path d='M2 6h10M5 1.5v3M9 1.5v3' stroke='currentColor' strokeWidth='1.3' />
  </svg>
);

/** One wrapping row above the results: the filters on the left, "Sort by" and the list / calendar toggle on the right. */
const ResultsToolbar: React.FC<ResultsToolbarProps> = ({
  children,
  display,
}) => (
  <div className={styles.resultsBar}>
    {children}
    {display && (
      <div className={styles.resultsBarEnd}>
        <label className={styles.sortLabel}>
          Sort by:{' '}
          <select
            className={styles.sortSelect}
            value={display.sort}
            aria-label='Sort by'
            onChange={(e) => display.onSortChange(e.target.value as SortKey)}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <div className={styles.viewToggle} role='group' aria-label='View'>
          <button
            type='button'
            aria-label='List view'
            aria-pressed={display.view === 'list'}
            className={display.view === 'list' ? styles.viewToggleActive : ''}
            onClick={() => display.onViewChange('list')}
          >
            <ListIcon />
          </button>
          <button
            type='button'
            aria-label='Calendar view'
            aria-pressed={display.view === 'calendar'}
            className={
              display.view === 'calendar' ? styles.viewToggleActive : ''
            }
            onClick={() => display.onViewChange('calendar')}
          >
            <CalendarIcon />
          </button>
        </div>
      </div>
    )}
  </div>
);

export default ResultsToolbar;
